/**
 * Apply AI-architect block operations to an EXISTING document — the engine
 * behind Ask Lilia's "update this document". Mirrors AiConversationPage's
 * persistOps but standalone (takes a documentId, fetches the live blocks),
 * so the in-editor Ask Lilia panel can rewrite the open document.
 *
 * Order: remove → edit → add (in final sequence) → reorder. The final reorder
 * is authoritative, so per-create sortOrder is only a hint.
 */
import { api, type BlockOp } from "@/lib/api";
import { applyOps, type SkelBlock } from "@/pages/AiConversationPage/aiConversationData";

export interface OpsSummary {
  added: number;
  edited: number;
  removed: number;
  moved: number;
  total: number;
}

export function summarizeArchitectOps(ops: BlockOp[]): OpsSummary {
  const s: OpsSummary = { added: 0, edited: 0, removed: 0, moved: 0, total: ops.length };
  for (const o of ops) {
    if (o.op === "add") s.added++;
    else if (o.op === "edit") s.edited++;
    else if (o.op === "remove") s.removed++;
    else if (o.op === "move") s.moved++;
  }
  return s;
}

export async function applyArchitectOps(documentId: string, ops: BlockOp[]): Promise<void> {
  const { blocks: current } = await api.blocks.list(documentId);
  const committed: SkelBlock[] = current.map((b) => ({
    uid: b.id,
    serverId: b.id,
    type: b.type,
    content: b.content as Record<string, unknown>,
    label: null,
    slot: "",
  }));
  const skeleton = applyOps(committed, ops);

  // 1) removes
  for (const op of ops) {
    if (op.op === "remove" && op.id) await api.blocks.delete(documentId, op.id).catch(() => {});
  }
  // 2) edits
  for (const op of ops) {
    if (op.op === "edit" && op.id && op.block) {
      await api.blocks.update(documentId, op.id, { type: op.block.type, content: op.block.content }).catch(() => {});
    }
  }
  // 3) adds, in the final sequence order
  const uidToServer = new Map<string, string>();
  let sort = 0;
  for (const b of skeleton) {
    if (b.diff === "remove") continue;
    if (b.diff === "add") {
      const { block } = await api.blocks.create(documentId, { type: b.type, content: b.content ?? {}, sortOrder: sort });
      uidToServer.set(b.uid, block.id);
    }
    sort++;
  }
  // 4) authoritative reorder
  const orderedIds = skeleton
    .filter((b) => b.diff !== "remove")
    .map((b) => b.serverId ?? uidToServer.get(b.uid))
    .filter((x): x is string => !!x);
  if (orderedIds.length > 0) await api.blocks.reorder(documentId, orderedIds).catch(() => {});
}

/** Whether a free-text message is asking Lilia to build/rewrite the document. */
export function isDocBuildIntent(text: string): boolean {
  const t = text.toLowerCase();
  const verb = /\b(write|create|build|draft|generate|make|compose|rewrite|replace|fill|populate|turn into|remove everything|start over|scaffold|outline)\b/.test(t);
  const obj = /\b(document|doc|paper|article|essay|report|thesis|chapter|section|sections|it|this|everything|draft|content)\b/.test(t);
  return verb && obj;
}
